import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getProducts, addProduct, updateProduct, deleteProduct, getCollections } from '../../firebase/database';
import { uploadProductImages } from '../../firebase/storage';
import { compressMultiple } from '../../utils/imageCompress';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const emptyProduct = { title:'', description:'', shortDescription:'', price:'', salePrice:'', stock:'', collectionId:'', images:[], isNew:false, metaTitle:'', metaDescription:'', features:[] };

// Build a clean initial state for the modal from a source product (or nothing for "Add").
// Always strips id/createdAt/updatedAt so no stale identifiers ever leak into a new record.
const buildInitialState = (source) => {
  if (!source) {
    return { form: { ...emptyProduct }, hasVariations: false, variationType: '', variationRows: [], imagePreviews: [], imageFiles: [] };
  }
  const { id: _id, createdAt: _ca, updatedAt: _ua, variations, images, ...rest } = source;
  const hasVariations = Array.isArray(variations) && variations.length > 0 && variations[0]?.values?.length > 0;
  return {
    form: { ...emptyProduct, ...rest },
    hasVariations,
    variationType: hasVariations ? (variations[0].name || '') : '',
    variationRows: hasVariations ? variations[0].values.map(v => ({
      label: v.label || '', price: v.price ?? '', salePrice: v.salePrice ?? '', stock: v.stock ?? '',
      imageFiles: [], imagePreviews: v.images || [],
    })) : [],
    imagePreviews: images || [],
    imageFiles: [],
  };
};

// ─── PRODUCT FORM MODAL — fully isolated, remounts fresh via `key` on every open ──────────────
const ProductFormModal = ({ mode, sourceProduct, collections, onClose, onSaved }) => {
  const init = buildInitialState(mode === 'add' ? null : sourceProduct);
  const [form, setForm] = useState(init.form);
  const [hasVariations, setHasVariations] = useState(init.hasVariations);
  const [variationType, setVariationType] = useState(init.variationType);
  const [variationRows, setVariationRows] = useState(init.variationRows);
  const [imageFiles, setImageFiles] = useState(init.imageFiles);
  const [imagePreviews, setImagePreviews] = useState(init.imagePreviews);
  const [featureInput, setFeatureInput] = useState('');
  const [saving, setSaving] = useState(false);

  const isEditing = mode === 'edit';
  const editingId = isEditing ? sourceProduct.id : null;

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const compressed = await compressMultiple(files);
    setImageFiles(prev => [...prev, ...compressed]);
    setImagePreviews(prev => [...prev, ...compressed.map(f => URL.createObjectURL(f))]);
  };

  const removeMainImage = (i) => {
    setImagePreviews(p => p.filter((_, j) => j !== i));
    setImageFiles(p => p.filter((_, j) => j !== i));
    setForm(f => ({ ...f, images: (f.images || []).filter((_, j) => j !== i) }));
  };

  const addVariationRow = () => setVariationRows(r => [...r, { label: '', price: '', salePrice: '', stock: '', imageFiles: [], imagePreviews: [] }]);
  const removeVariationRow = (i) => setVariationRows(r => r.filter((_, j) => j !== i));
  const updateVariationRow = (i, field, val) => setVariationRows(r => r.map((row, j) => j === i ? { ...row, [field]: val } : row));

  const handleVariationImageChange = async (i, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const compressed = await compressMultiple(files);
    const previews = compressed.map(f => URL.createObjectURL(f));
    setVariationRows(r => r.map((row, j) => j === i ? {
      ...row,
      imageFiles: [...(row.imageFiles || []), ...compressed],
      imagePreviews: [...(row.imagePreviews || []), ...previews],
    } : row));
  };

  const removeVariationImage = (rowIdx, imgIdx) => {
    setVariationRows(r => r.map((row, j) => j === rowIdx ? {
      ...row,
      imageFiles: (row.imageFiles || []).filter((_, k) => k !== imgIdx),
      imagePreviews: (row.imagePreviews || []).filter((_, k) => k !== imgIdx),
    } : row));
  };

  const addFeature = () => { if (featureInput.trim()) { setForm(f => ({ ...f, features: [...(f.features || []), featureInput.trim()] })); setFeatureInput(''); } };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, j) => j !== i) }));

  const handleSave = async () => {
    if (!form.title?.trim()) { toast.error('Product title is required'); return; }
    if (!form.price) { toast.error('Price is required'); return; }
    if (hasVariations && !variationType.trim()) { toast.error('Please name the variation type (e.g. Color, Size)'); return; }
    if (hasVariations && variationRows.length === 0) { toast.error('Add at least one variation value'); return; }
    if (hasVariations && variationRows.some(r => !r.label.trim())) { toast.error('Every variation value needs a name'); return; }

    setSaving(true);
    try {
      // Use a temp id only for organizing storage paths; never written into the record itself.
      const storagePathId = editingId || `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      let imageUrls = form.images || [];
      if (imageFiles.length > 0) {
        const newUrls = await uploadProductImages(imageFiles, storagePathId);
        imageUrls = [...imageUrls, ...newUrls];
      }

      let processedVariations = [];
      if (hasVariations) {
        const processedValues = [];
        for (const row of variationRows) {
          let vImages = (row.imagePreviews || []).filter(p => p.startsWith('http'));
          if (row.imageFiles?.length > 0) {
            const safeLabel = (row.label || 'value').replace(/[^a-z0-9]+/gi, '_');
            const vUrls = await uploadProductImages(row.imageFiles, `${storagePathId}_var_${safeLabel}`);
            vImages = [...vImages, ...vUrls];
          }
          processedValues.push({
            label: row.label.trim(),
            price: row.price ? parseFloat(row.price) : null,
            salePrice: row.salePrice ? parseFloat(row.salePrice) : null,
            stock: row.stock ? parseInt(row.stock) : 0,
            images: vImages,
          });
        }
        processedVariations = [{ name: variationType.trim(), values: processedValues }];
      }

      const data = {
        title: form.title.trim(),
        description: form.description || '',
        shortDescription: form.shortDescription || '',
        price: parseFloat(form.price),
        salePrice: form.salePrice ? parseFloat(form.salePrice) : null,
        stock: hasVariations ? variationRows.reduce((s, r) => s + (parseInt(r.stock) || 0), 0) : (form.stock ? parseInt(form.stock) : 0),
        collectionId: form.collectionId || '',
        images: imageUrls,
        variations: processedVariations,
        isNew: !!form.isNew,
        metaTitle: form.metaTitle || '',
        metaDescription: form.metaDescription || '',
        features: form.features || [],
        rating: isEditing ? (sourceProduct.rating || 0) : 0,
        reviewCount: isEditing ? (sourceProduct.reviewCount || 0) : 0,
      };

      if (isEditing) {
        await updateProduct(editingId, data);
        toast.success('Product updated!');
      } else {
        await addProduct(data);
        toast.success(mode === 'duplicate' ? 'Duplicate created as new product!' : 'Product added!');
      }
      onSaved();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const titleText = mode === 'edit' ? 'Edit Product' : mode === 'duplicate' ? 'Duplicate Product (New)' : 'Add New Product';

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        className="fixed inset-4 md:inset-8 bg-white z-50 overflow-y-auto shadow-2xl flex flex-col rounded-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <h2 className="font-display text-xl font-black text-gray-900">{titleText}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="flex-1 p-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* MAIN COLUMN */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="border border-gray-100 rounded-2xl p-5">
                <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center text-xs">1</span>
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Product Title *</label>
                    <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="input-field" placeholder="e.g. Premium Plastic Chair" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Short Description</label>
                    <input value={form.shortDescription} onChange={e => setForm(f => ({ ...f, shortDescription: e.target.value }))} className="input-field" placeholder="Brief one-line description" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Description</label>
                    <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input-field resize-none" placeholder="Detailed product description..." />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="border border-gray-100 rounded-2xl p-5">
                <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center text-xs">2</span>
                  Pricing & Stock
                </h3>
                <div className="flex gap-2 mb-4 bg-gray-50 p-1 rounded-xl">
                  <button onClick={() => setHasVariations(false)} className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${!hasVariations ? 'bg-white shadow-sm text-[#1a3a6e]' : 'text-gray-400'}`}>Simple Product</button>
                  <button onClick={() => setHasVariations(true)} className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${hasVariations ? 'bg-white shadow-sm text-[#1a3a6e]' : 'text-gray-400'}`}>Has Variations (Color, Size, etc.)</button>
                </div>

                {!hasVariations ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Price (PKR) *</label>
                      <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="input-field" placeholder="0" /></div>
                    <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Sale Price</label>
                      <input type="number" value={form.salePrice || ''} onChange={e => setForm(f => ({ ...f, salePrice: e.target.value }))} className="input-field" placeholder="Optional" /></div>
                    <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Stock</label>
                      <input type="number" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} className="input-field" placeholder="0" /></div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3"><label className="block text-xs font-bold text-gray-700 mb-1.5">What is this variation? *</label>
                      <input value={variationType} onChange={e => setVariationType(e.target.value)} className="input-field" placeholder="e.g. Color, Size, Material" /></div>
                    <div className="mb-2"><label className="block text-xs font-bold text-gray-700 mb-1.5">Default Price (PKR) — used as base *</label>
                      <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="input-field" placeholder="Base price" /></div>

                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Variation Values</p>
                        <button onClick={addVariationRow} className="text-xs font-bold text-[#1a3a6e] hover:text-[#e63946] transition-colors flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                          Add Value
                        </button>
                      </div>

                      {variationRows.length === 0 && (
                        <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl">
                          <p className="text-gray-400 text-sm mb-2">No values added yet</p>
                          <button onClick={addVariationRow} className="btn-primary text-xs py-2 px-4">+ Add First Value</button>
                        </div>
                      )}

                      {variationRows.map((row, i) => (
                        <div key={i} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                          <div className="flex flex-col sm:flex-row items-start gap-3">
                            <div className="flex items-start gap-3 w-full sm:w-auto">
                              <div className="flex-shrink-0">
                                <label className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-[#1a3a6e] transition-colors bg-white overflow-hidden relative">
                                  {row.imagePreviews?.length > 0 ? (
                                    <img src={row.imagePreviews[0]} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                  )}
                                  {row.imagePreviews?.length > 1 && <span className="absolute bottom-0 right-0 bg-[#1a3a6e] text-white text-[9px] px-1 font-bold rounded-tl">+{row.imagePreviews.length - 1}</span>}
                                  <input type="file" multiple accept="image/*" className="hidden" onChange={e => handleVariationImageChange(i, e)} />
                                </label>
                              </div>
                              {/* Remove button sits next to the thumbnail only on mobile, where it's not at the far right of a row */}
                              <button onClick={() => removeVariationRow(i)} className="sm:hidden text-red-400 hover:text-red-600 transition-colors p-1.5 flex-shrink-0 ml-auto">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                              </button>
                            </div>
                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                              <div><label className="block text-[10px] font-bold text-gray-500 mb-1">Name *</label>
                                <input value={row.label} onChange={e => updateVariationRow(i, 'label', e.target.value)} className="input-field text-xs py-2" placeholder="e.g. Red" /></div>
                              <div><label className="block text-[10px] font-bold text-gray-500 mb-1">Price</label>
                                <input type="number" value={row.price} onChange={e => updateVariationRow(i, 'price', e.target.value)} className="input-field text-xs py-2" placeholder="Same as base" /></div>
                              <div><label className="block text-[10px] font-bold text-gray-500 mb-1">Sale Price</label>
                                <input type="number" value={row.salePrice} onChange={e => updateVariationRow(i, 'salePrice', e.target.value)} className="input-field text-xs py-2" placeholder="Optional" /></div>
                              <div><label className="block text-[10px] font-bold text-gray-500 mb-1">Stock</label>
                                <input type="number" value={row.stock} onChange={e => updateVariationRow(i, 'stock', e.target.value)} className="input-field text-xs py-2" placeholder="0" /></div>
                            </div>
                            <button onClick={() => removeVariationRow(i)} className="hidden sm:block text-red-400 hover:text-red-600 transition-colors p-1.5 flex-shrink-0 mt-5">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                            </button>
                          </div>
                          {row.imagePreviews?.length > 0 && (
                            <div className="flex gap-1.5 mt-3 flex-wrap sm:pl-[76px]">
                              {row.imagePreviews.map((src, ii) => (
                                <div key={ii} className="relative w-10 h-10">
                                  <img src={src} alt="" className="w-full h-full object-cover rounded-lg" />
                                  <button onClick={() => removeVariationImage(i, ii)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="border border-gray-100 rounded-2xl p-5">
                <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-gray-300 text-white rounded-full flex items-center justify-center text-xs">+</span>
                  Key Features (optional)
                </h3>
                <div className="flex gap-2 mb-2">
                  <input value={featureInput} onChange={e => setFeatureInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    className="input-field flex-1" placeholder="Type feature and press Enter" />
                  <button onClick={addFeature} className="bg-[#1a3a6e] text-white px-4 text-sm font-bold hover:bg-[#e63946] transition-colors rounded-lg">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.features?.map((ft, i) => (
                    <span key={i} className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-full">
                      {ft}
                      <button onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="space-y-5">
              <div className="bg-gray-50 p-4 rounded-xl space-y-3">
                <h3 className="font-bold text-sm text-gray-700">Organize</h3>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Collection</label>
                  <select value={form.collectionId || ''} onChange={e => setForm(f => ({ ...f, collectionId: e.target.value }))} className="input-field">
                    <option value="">No Collection</option>
                    {collections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isNew || false} onChange={e => setForm(f => ({ ...f, isNew: e.target.checked }))} className="accent-[#1a3a6e] w-4 h-4" />
                  <span className="text-sm font-medium text-gray-700">Mark as New Arrival</span>
                </label>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-bold text-sm text-gray-700 mb-3">Product Images</h3>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 p-6 cursor-pointer hover:border-[#1a3a6e] transition-colors rounded-xl">
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  <span className="text-xs text-gray-500">Click to upload images</span>
                  <input type="file" multiple accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {imagePreviews.map((src, i) => (
                      <div key={i} className="relative aspect-square">
                        <img src={src} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button onClick={() => removeMainImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <details className="bg-gray-50 p-4 rounded-xl">
                <summary className="font-bold text-sm text-gray-700 cursor-pointer">SEO Settings (optional)</summary>
                <div className="space-y-3 mt-3">
                  <div><label className="block text-xs font-bold text-gray-700 mb-1">Meta Title</label><input value={form.metaTitle} onChange={e => setForm(f => ({ ...f, metaTitle: e.target.value }))} className="input-field text-sm" /></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1">Meta Description</label><textarea rows={2} value={form.metaDescription} onChange={e => setForm(f => ({ ...f, metaDescription: e.target.value }))} className="input-field resize-none text-sm" maxLength={160} /></div>
                </div>
              </details>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} className="btn-outline text-sm py-2.5 px-6">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2">
            {saving && <Spinner size="sm" color="white" />}
            {saving ? 'Saving...' : isEditing ? 'Update Product' : 'Save Product'}
          </button>
        </div>
      </motion.div>
    </>
  );
};

// ─── MAIN ADMIN PRODUCTS PAGE ─────────────────────────────────────────────────
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const searchBoxRef = useRef(null);

  const [modalState, setModalState] = useState(null);

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([getProducts(), getCollections()]);
      setProducts(p);
      setCollections(c);
    } catch (err) {
      console.error('Failed to load products:', err);
      toast.error('Failed to load products. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openAdd = () => setModalState({ mode: 'add', product: null, key: Date.now() });
  const openEdit = (p) => setModalState({ mode: 'edit', product: p, key: Date.now() + Math.random() });
  const openDuplicate = (p) => setModalState({ mode: 'duplicate', product: p, key: Date.now() + Math.random() });
  const closeModal = () => setModalState(null);
  const handleSaved = () => { closeModal(); loadData(); };

  const handleDelete = async (id, title) => {
    if (!id) { toast.error('Invalid product — cannot delete'); return; }
    if (!window.confirm(`Delete "${title || 'this product'}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      toast.success('Product deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete: ' + (err.message || 'Check your admin permissions.'));
    } finally {
      setDeletingId(null);
    }
  };

  const searchLower = search.trim().toLowerCase();
  const suggestions = searchLower
    ? products.filter(p => p.title?.toLowerCase().includes(searchLower)).slice(0, 6)
    : [];
  const filtered = products.filter(p => !searchLower || p.title?.toLowerCase().includes(searchLower));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl font-black text-gray-900">Products</h1><p className="text-gray-500 text-sm">{products.length} total</p></div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2 text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
          Add Product
        </button>
      </div>

      {/* Search with live suggestions */}
      <div ref={searchBoxRef} className="relative mb-5 max-w-xs">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 z-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          className="input-field pl-9"
          placeholder="Search products..."
        />
        <AnimatePresence>
          {showSuggestions && searchLower && suggestions.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 shadow-xl rounded-xl overflow-hidden z-30">
              {suggestions.map(p => (
                <button key={p.id} onClick={() => { setSearch(p.title); setShowSuggestions(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left">
                  <img src={p.images?.[0] || 'https://via.placeholder.com/32'} alt="" className="w-8 h-8 object-cover rounded-lg flex-shrink-0" onError={e => e.target.src = 'https://via.placeholder.com/32'} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.title}</p>
                    <p className="text-xs text-gray-400">PKR {p.price?.toLocaleString()}</p>
                  </div>
                </button>
              ))}
            </motion.div>
          )}
          {showSuggestions && searchLower && suggestions.length === 0 && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 shadow-xl rounded-xl overflow-hidden z-30 p-4 text-center">
              <p className="text-sm text-gray-400">No products match "{search}"</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{['Product', 'Collection', 'Price', 'Stock', 'Variations', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(p => (
                  <motion.tr key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 overflow-hidden rounded-lg flex-shrink-0">
                          <img src={p.images?.[0] || 'https://via.placeholder.com/40'} alt={p.title} className="w-full h-full object-cover" onError={e => e.target.src = 'https://via.placeholder.com/40'} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{p.title}</p>
                          {p.isNew && <span className="text-[10px] bg-[#1a3a6e] text-white px-1.5 py-0.5 font-bold rounded">NEW</span>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{collections.find(c => c.id === p.collectionId)?.name || '—'}</td>
                    <td className="py-3 px-4">
                      <p className="font-bold text-gray-900">PKR {p.price?.toLocaleString()}</p>
                      {p.salePrice && <p className="text-xs text-green-600">Sale: PKR {p.salePrice?.toLocaleString()}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-semibold text-sm ${p.stock > 10 ? 'text-green-600' : p.stock > 0 ? 'text-orange-500' : 'text-red-500'}`}>{p.stock}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{p.variations?.[0]?.values?.length > 0 ? `${p.variations[0].name}: ${p.variations[0].values.length}` : '—'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(p)} title="Edit" className="p-1.5 text-[#1a3a6e] hover:bg-[#1a3a6e]/10 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                        <button onClick={() => openDuplicate(p)} title="Duplicate as new product" className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                        </button>
                        <button onClick={() => handleDelete(p.id, p.title)} disabled={deletingId === p.id} title="Delete" className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40">
                          {deletingId === p.id ? <Spinner size="sm" /> : <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                {search ? `No products match "${search}"` : 'No products yet — click Add Product to create one.'}
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalState && (
          <ProductFormModal
            key={modalState.key}
            mode={modalState.mode}
            sourceProduct={modalState.product}
            collections={collections}
            onClose={closeModal}
            onSaved={handleSaved}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
export default AdminProducts;
